import { channelColorMap, chartAllColors } from '@op/constants';
import { useCancelRequestHook } from '@op/hooks/useCancelRequest';
import {
  getGameStoreStatsInfo,
  getNewsChannel,
  getNewsLanguage,
  getOverviewChannel,
  getOverviewCountry,
  getOverviewLanguage,
} from '@op/services';
import {
  getRationPercentValue,
  handleMentionData,
  sortArrayAuxiliaryFunc,
  sortMapAuxiliaryFunc,
  supplementSingleData,
} from '@op/utils';
import { getNeedTime } from '@op/utils/time';
import { useEffect, useMemo, useState } from 'react';

export type RequestFuncMapType =
  | 'language'
  | 'channel'
  | 'language_news'
  | 'channel_news'
  | 'country'
  | 'language_rating';
const requestFuncMap: { [key in RequestFuncMapType]: any } = {
  language: { func: getOverviewLanguage, sort: 'count' },
  channel: { func: getOverviewChannel, sort: 'count' },
  language_news: { func: getNewsLanguage, sort: 'count' },
  channel_news: { func: getNewsChannel, sort: 'count' },
  country: { func: getOverviewCountry, sort: 'count' },
  language_rating: { func: getGameStoreStatsInfo, sort: 'score' },
};

/**
 *
 * @param param0
 * @returns
 */
export function useOverviewChannelOrLanguageInfo({
  filter,
  type = 'channel',
  isNotReq,
  isSteam = false,
}: {
  filter: any;
  type: RequestFuncMapType;
  isNotReq?: boolean;
  isSteam?: boolean;
}) {
  const [timeline, setTimeline] = useState<any>({});
  const [pie, setPie] = useState<any>({});
  const [bar, setBar] = useState<any>({});
  const [table, setTable] = useState<any>([]);
  const [rating, setRating] = useState<any>({});

  const { loading, wrapperRequest, error } = useCancelRequestHook();

  function sortDataList(list: Array<any>, sort: string) {
    return (list || []).sort((a, b) => {
      if (a.name?.toLocaleLowerCase() === 'others') return 1; // Move 'Others' to the end
      if (b.name?.toLocaleLowerCase() === 'others') return -1; // Move 'others' to the end
      return b[sort] - a[sort]; // Sort by count in ascending order
    });
  }

  function init() {
    setTimeline({});
    setPie({});
    setBar({});
    setTable([]);
    setRating({});
  }

  function requestApi(requestFunc: any, sort: string) {
    if (isNotReq) return;
    if (!filter.edition_unified_id) return;
    init();
    wrapperRequest(cancelFunc =>
      requestFunc(filter, cancelFunc)
        .then((data: any) => {
          const realData = Array.isArray(data) ? data[0] : data;
          const {
            mentions,
            sum,
            timeline_of_start_time,
            timeline_of_end_time,
            timeline_of_mentions,
            new_list,
            old_mentions,
            link_of_end_time,
            link_of_start_time,
          } = realData || {};

          const sortList = sortDataList(mentions, sort);

          const { bar, pie, table } = dealwithBarAndPieData({
            sortList,
            oldList: old_mentions,
          });
          const timelineChartData = dealwithTimelineData({
            timeline_of_start_time,
            timeline_of_end_time,
            timeline_of_mentions,
            sortList,
          });

          setBar(bar);
          setTimeline(timelineChartData);
          setPie(pie);
          setTable(
            table.map(val => ({
              ...val,
              oldTime: [link_of_start_time, link_of_end_time],
              newTime: [filter?.start_time, filter?.end_time],
            })),
          );

          if (new_list) {
            sortDataList(new_list, sort);
            const { rating } = dealWithRatingData({ data: new_list });
            setRating(rating);
          }
        })
        .catch(() => {
          init();
        }),
    );
  }

  function dealwithTimelineData({
    timeline_of_start_time,
    timeline_of_end_time,
    timeline_of_mentions,
    sortList,
  }: {
    timeline_of_start_time: string;
    timeline_of_end_time: string;
    timeline_of_mentions: Array<any>;
    sortList: Array<any>;
  }) {
    const newData = supplementSingleData(
      timeline_of_start_time,
      timeline_of_end_time,
      timeline_of_mentions,
      filter.date_type,
    );
    const needTime = getNeedTime(filter.date_type);
    let { newArr, timeArr } = handleMentionData(newData, needTime);

    const sortMap = sortList.reduce((acc, cur, index) => {
      acc[cur.name] = index;
      return acc;
    }, {});

    newArr.sort(sortMapAuxiliaryFunc(sortMap, item => item.name));

    const legendData: Array<any> = [];
    newArr = newArr.map((v, index) => {
      const color = channelColorMap[v.name] || chartAllColors[index];
      legendData.push({
        name: v.name,
        icon: 'circle',
        itemStyle: { color },
      });
      return { ...v, itemStyle: { color } };
    });

    return {
      newArr,
      timeArr,
      legendData,
    };
  }

  function calculatePercentage(mentions: number, count: number) {
    return count > 0 ? mentions / count : 0;
  }

  function flattenObject(obj: any) {
    if (!obj || typeof obj !== 'object') {
      return obj;
    }

    return Object.keys(obj).reduce((acc: Record<string, any>, key: string) => {
      const value = obj[key];

      if (
        typeof value === 'object' &&
        value !== null &&
        !Array.isArray(value)
      ) {
        let total = 0;
        const flattenedSubObj = Object.keys(value).reduce(
          (subAcc: Record<string, any>, subKey: string) => {
            const subValue = value[subKey];
            subAcc[`${key}_${subKey}`] = subValue;

            if (typeof subValue === 'number') {
              total += subValue;
            }

            return subAcc;
          },
          {},
        );
        Object.assign(acc, flattenedSubObj);

        if (total !== 0) {
          acc[`${key}_total`] = total;
        }
      } else {
        acc[key] = value;
      }

      return acc;
    }, {});
  }

  function dealwithBarAndPieData({
    sortList = [],
    oldList = [],
  }: {
    sortList: Array<any>;
    oldList: Array<any>;
  }) {
    const dataY: Array<any> = [];
    const negativeMentions: Array<any> = [];
    const neutralMentions: Array<any> = [];
    const positiveMentions: Array<any> = [];
    const starRatingsMap: Array<any> = [];
    const total: Array<any> = [];
    const sentimentList: Array<any> = [];
    let mentionsTotal = 0;
    const pie: Array<any> = [];
    const legendData: Array<any> = [];
    const tableData: Array<any> = [];

    const lifetimePositiveMention: Array<any> = [];
    const positiveMentionsPercent: Array<any> = [];
    const positiveDifference: Array<any> = [];
    sortList.forEach((item: any, index: number) => {
      const {
        negative_mentions,
        neutral_mentions,
        positive_mentions,
        count: countValue,
        name,
        lifetime_positive_mention,
        lifetime_negative_mention,
        avg_sentiment,
        ...resItem
      } = item || {};

      const oldInfo = oldList?.find(val => val.name === name);
      const count = isSteam
        ? (negative_mentions || 0) + (positive_mentions || 0)
        : countValue;
      const oldCount = isSteam
        ? (oldInfo?.negative_mentions || 0) + (oldInfo?.positive_mentions || 0)
        : oldInfo?.count;
      // 总量
      const lifetimeTotal =
        (lifetime_negative_mention || 0) + (lifetime_positive_mention || 0);

      dataY.unshift(name);
      negativeMentions.unshift({
        value: -negative_mentions,
        originName: name,
        total: count,
      });
      neutralMentions.unshift({
        value: neutral_mentions,
        originName: name,
        total: count,
      });
      positiveMentions.unshift({
        value: positive_mentions,
        originName: name,
        total: count,
      });

      const lifetimePositiveRatio = calculatePercentage(
        lifetime_positive_mention,
        lifetimeTotal,
      );
      lifetimePositiveMention.unshift({
        value: lifetimePositiveRatio,
        originName: name,
        total: lifetimeTotal,
      });

      // 过滤量
      const positiveRatio = calculatePercentage(positive_mentions, count);
      positiveMentionsPercent.unshift({
        value: -positiveRatio,
        originName: name,
        total: count,
      });

      // 差量
      positiveDifference.unshift({
        value: positiveRatio - lifetimePositiveRatio,
        originName: name,
        total: count,
      });

      sentimentList.unshift(avg_sentiment);
      total.unshift(count);
      mentionsTotal += count;

      const color = channelColorMap[name] || chartAllColors[index];
      legendData.push({
        name: name,
        icon: 'circle',
        itemStyle: { color },
      });

      if (resItem?.star_ratings) {
        Object.entries(resItem.star_ratings).forEach(([key, value]) => {
          const arrayName = `starRatings${key}`;
          if (!starRatingsMap[arrayName]) {
            starRatingsMap[arrayName] = [];
          }
          starRatingsMap[arrayName].unshift({
            value: value || 0,
            originName: name,
            total: count,
          });
        });
      }

      const extraData = flattenObject(resItem);
      pie.push({
        name: name,
        value: count || 0,
        itemStyle: { color },
        ...(extraData || {}),
      });

      tableData.push({
        ...item,
        negative: calculatePercentage(negative_mentions, count),
        positive: calculatePercentage(positive_mentions, count),
        neutral: calculatePercentage(neutral_mentions, count),
        lifetime_negative: calculatePercentage(
          lifetime_negative_mention,
          lifetimeTotal,
        ),
        lifetime_positive: calculatePercentage(
          lifetime_positive_mention,
          lifetimeTotal,
        ),
        count_change: getRationPercentValue(count, oldCount, 6) / 100,
        count,
        old_count: oldCount,
        ...(extraData || {}),
      });
    });

    return {
      pie: {
        data: pie,
        legend: legendData,
        mentionsTotal,
      },
      bar: {
        dataY,
        negativeMentions,
        neutralMentions,
        positiveMentions,
        total,
        mentionsTotal,
        lifetimePositiveMention,
        positiveMentionsPercent,
        positiveDifference,
        sentimentList,
        ...starRatingsMap,
      },
      table: tableData,
      mentionsTotal,
    };
  }

  function dealWithRatingData({ data = [] }: { data: Array<any> }) {
    const dataY: Array<any> = [];
    const detailRatingMap: Array<any> = [];
    const tableData: Array<any> = [];
    const scoreArr: Array<any> = [];
    const lifetimeScore: Array<any> = [];
    const total: Array<any> = [];
    let mentionsTotal = 0;

    data?.sort((a: any, b: any) => {
      if (a.language?.toLocaleLowerCase() === 'others') return 1; // Move 'Others' to the end
      if (b.language?.toLocaleLowerCase() === 'others') return -1; // Move 'others' to the end
      return b.score - a.score; // Sort by count in ascending order
    });

    data?.forEach((item: any) => {
      const { language: name, score, lifetime_score, mentions } = item;
      dataY.unshift(name);
      if (item?.detail) {
        Object.entries(item.detail).forEach(([key, value]) => {
          const arrayName = `starRatings${key}`;
          if (!detailRatingMap[arrayName]) {
            detailRatingMap[arrayName] = [];
          }
          detailRatingMap[arrayName].unshift({
            value: value || 0,
            originName: name,
          });
        });
      }
      total.unshift({
        value: score - lifetime_score,
        originName: name,
      });
      mentionsTotal += mentions || 0;

      scoreArr.unshift({
        value: -score,
        originName: name,
      });

      lifetimeScore.unshift({
        value: lifetime_score,
        originName: name,
      });

      tableData.push({ ...item });
    });

    return {
      rating: {
        dataY,
        tableData,
        scoreArr,
        lifetimeScore,
        ...detailRatingMap,
        total,
        mentionsTotal,
      },
    };
  }

  useEffect(() => {
    const { func: requestFunc, sort } = requestFuncMap[type];
    requestApi(requestFunc, sort);
  }, [filter, type]);

  const chartData = useMemo(() => {
    return { pie, bar, timeline, rating };
  }, [pie, bar, timeline, rating]);

  return {
    chartData,
    tableData: table,
    loading,
    empty: (pie.mentionsTotal ?? 0) === 0 && (rating?.mentionsTotal ?? 0) === 0,
    error,
  };
}
